import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getPromocodeAnalytics } from "../../api/context/analytics";
import { FiInfo, FiRefreshCw, FiTag, FiUsers, FiGift, FiActivity } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const DEFAULT_LIMIT = 15;

const formatCurrency = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(number);
};

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PromoAnalytics = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());

    const getTodayDate = () => new Date().toISOString().split('T')[0];

    const summary = analytics?.summary || {};
    const topPromocodes = Array.isArray(analytics?.topPromocodes) ? analytics.topPromocodes : [];
    const promocodes = Array.isArray(analytics?.promocodes) ? analytics.promocodes : [];
    const redemptionTrends = Array.isArray(analytics?.redemptionTrends) ? analytics.redemptionTrends : [];

    const usedPromocodes = topPromocodes.filter(p => Number(p.usageCount) > 0);
    const chartData = usedPromocodes.slice(0, 10);

    const fetchAnalytics = async (isRefresh = false, overrideFilters) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const effective = overrideFilters || filters;
        const requestBody = {
            limit: DEFAULT_LIMIT,
            startDate: effective.startDate,
            endDate: effective.endDate,
        };

        try {
            await new Promise((resolve, reject) => {
                getPromocodeAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);

                        const start = new Date(effective.startDate);
                        const end = new Date(effective.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                        toast.success(`Promo analytics fetched for ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`);
                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error('Failed to fetch promocode analytics'));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching promocode analytics:", error);
            setAnalytics(null);
            toast.error('Failed to fetch promocode analytics data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate') {
            if (value > today) return toast.error('Start date cannot be in the future');
            if (filters.endDate && value > filters.endDate) return toast.error('Start date cannot be after end date');
        }

        if (key === 'endDate') {
            if (value > today) return toast.error('End date cannot be in the future');
            if (filters.startDate && value < filters.startDate) return toast.error('End date cannot be before start date');
        }

        setFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => fetchAnalytics(true), 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleClearFilters = () => {
        const defaults = getDefaultDates();
        setFilters(defaults);
        setTimeout(() => fetchAnalytics(true, defaults), 100);
    };

    const isFilterActive = () => {
        const defaults = getDefaultDates();
        return filters.startDate !== defaults.startDate || filters.endDate !== defaults.endDate;
    };

    if (loading && !refreshing) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Loader />
            </div>
        );
    }

    if (!loading && !analytics) {
        return (
            <div className="flex flex-col items-center justify-center p-10 text-center text-gray-600">
                <FiInfo className="text-5xl mb-3 text-gray-400" />
                <h2 className="font-bold text-2xl">No Data Available</h2>
                <p className="mt-2 text-lg text-gray-500 max-w-md">
                    We couldn't find any promocode analytics for the selected period.
                </p>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4 min-vh-100">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center mb-0">
                    <div
                        onClick={() => navigate(-1)}
                        style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center', marginRight: '15px' }}
                    >
                        <IoMdArrowBack className='icon' />
                    </div>
                    <h4 className="mb-0 fw-bold">Promo Analytics</h4>
                </div>

                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            style={{ width: '140px' }}
                            max={filters.endDate || getTodayDate()}
                        />

                        <div className="text-muted small">to</div>

                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            style={{ width: '140px' }}
                            min={filters.startDate || undefined}
                            max={getTodayDate()}
                        />

                        {isFilterActive() && (
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-sm btn-outline-secondary"
                                disabled={refreshing}
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn border border-secondary bg-transparent rounded-3 p-2"
                        style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        disabled={refreshing}
                    >
                        <FiRefreshCw
                            className="text-secondary"
                            size={18}
                            style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}
                        />
                    </button>
                </div>
            </div>

            {/* Date range chip */}
            {summary?.dateRange && (
                <div className="mb-3">
                    <small className="text-muted">
                        Showing data from <strong>{formatDate(summary.dateRange.startDate)}</strong> to <strong>{formatDate(summary.dateRange.endDate)}</strong>
                    </small>
                </div>
            )}

            {/* Summary Cards */}
            <div className="row g-4 mb-3">
                {[
                    {
                        title: "Total Redemptions",
                        value: summary.totalRedemptions ?? 0,
                        icon: <FiActivity className="text-secondary opacity-75" />,
                    },
                    {
                        title: "Unique Users",
                        value: summary.totalUniqueUsers ?? 0,
                        icon: <FiUsers className="text-secondary opacity-75" />,
                    },
                    {
                        title: "Codes Used",
                        value: `${summary.totalUniqueCodesUsed ?? 0} / ${summary.totalOffers ?? 0}`,
                        icon: <FiTag className="text-secondary opacity-75" />,
                    },
                    {
                        title: "Offers Used In Range",
                        value: summary.offersUsedInRange ?? 0,
                        icon: <FiGift className="text-secondary opacity-75" />,
                    },
                ].map((card, i) => (
                    <div key={i} className="col-md-3 col-sm-6">
                        <div className="card h-100 border border-secondary rounded-3">
                            <div className="card-body p-3 align-items-center position-relative">
                                <div className="position-absolute top-0 end-0 p-3">
                                    {React.cloneElement(card.icon, {
                                        className: `${card.icon.props.className} fs-5`
                                    })}
                                </div>
                                <h6 className="card-title mb-2 fw-semibold text-muted">{card.title}</h6>
                                <p className="fs-5 mb-0" style={{ fontWeight: '600' }}>{card.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Top Promocodes - chart + list */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card border-0 rounded-3">
                        <div className="p-0">
                            <h5 className="fw-bold mb-3 text-start">Top Promocodes by Usage</h5>

                            {chartData.length > 0 ? (
                                <div className="row g-4">
                                    <div className="col-md-8 bg-light rounded-3">
                                        <div style={{ width: "100%", height: 320, padding: 0, marginTop: 40, marginBottom: 30, paddingRight: 30 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart
                                                    data={chartData}
                                                    margin={{ top: 0, right: 0, left: 0, bottom: 20 }}
                                                    barCategoryGap="40%"
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis
                                                        dataKey="code"
                                                        angle={-40}
                                                        textAnchor="end"
                                                        interval={0}
                                                        tick={{ fontSize: 12, fontWeight: 'bold' }}
                                                    />
                                                    <YAxis tick={{ fontSize: 12, fontWeight: 'bold' }} />
                                                    <Tooltip cursor={{ fill: "transparent" }} />
                                                    <Bar
                                                        dataKey="usageCount"
                                                        name="Redemptions"
                                                        fill="#8B5CF6"
                                                        radius={[6, 6, 0, 0]}
                                                        barSize={40}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div
                                            className="border rounded-3 h-100"
                                            style={{
                                                borderColor: '#e9ecef',
                                                maxHeight: 380,
                                                overflowY: 'auto',
                                                backgroundColor: '#f8f9fa'
                                            }}
                                        >
                                            <div className="p-3">
                                                <h6 className="fw-semibold mb-3 text-center text-muted">Most Redeemed Codes</h6>
                                                <div className="d-flex flex-column gap-2">
                                                    {usedPromocodes.map((promo, index) => (
                                                        <div
                                                            key={promo.offerId}
                                                            className="d-flex justify-content-between align-items-center p-2 rounded-2"
                                                            style={{ backgroundColor: 'white', border: '1px solid #f1f1f1' }}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <span className="badge bg-light text-dark me-2">#{index + 1}</span>
                                                                <span
                                                                    className="fw-medium text-truncate"
                                                                    style={{ maxWidth: '140px' }}
                                                                    title={promo.name || promo.code}
                                                                >
                                                                    {promo.code}
                                                                </span>
                                                            </div>
                                                            <span className="fw-bold" style={{ color: '#8B5CF6' }}>
                                                                {promo.usageCount}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-muted">No promocodes have been redeemed in this period.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Redemption Trends */}
                <div className="col-12">
                    <div className="card border-0 rounded-3">
                        <div>
                            <h5 className="fw-bold mb-3">Redemption Trends</h5>
                            {redemptionTrends.length > 0 ? (
                                <div className="bg-light" style={{ width: "100%", height: 350 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={redemptionTrends} margin={{ top: 50, bottom: 50, right: 30 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="date"
                                                angle={-40}
                                                textAnchor="end"
                                                tick={{ fontSize: 12, fontWeight: 'bold' }}
                                                tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            />
                                            <YAxis tick={{ fontSize: 12, fontWeight: 'bold' }} />
                                            <Tooltip
                                                content={({ active, payload, label }) => {
                                                    if (active && payload && payload.length) {
                                                        return (
                                                            <div
                                                                className="p-2 rounded-2 shadow-sm"
                                                                style={{
                                                                    background: "#fff",
                                                                    border: "1px solid #ddd",
                                                                    fontSize: "12px",
                                                                    lineHeight: "1.4"
                                                                }}
                                                            >
                                                                <p className="fw-bold mb-1">{formatDate(label)}</p>
                                                                {payload.map((item, index) => (
                                                                    <p key={index} style={{ color: item.stroke, margin: 0 }}>
                                                                        {item.name}: <strong>{item.value}</strong>
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="dailyRedemptions"
                                                name="Daily Redemptions"
                                                stroke="#8B5CF6"
                                                strokeWidth={2}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="uniqueUsers"
                                                name="Unique Users"
                                                stroke="#06aebaff"
                                                strokeWidth={2}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <p className="text-muted">No redemption trend data for this period.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Promocodes Table */}
                <div className="col-12">
                    <div className="card border-0 rounded-3 h-100 overflow-auto">
                        <div>
                            <h5 className="fw-bold mb-4">All Promocodes</h5>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover text-center align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {"Code,Name,Discount,Min Cart,Redemptions,Unique Users,Status,Expires".split(",").map((h, i) => (
                                                <th key={i}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {promocodes.map((promo) => (
                                            <tr key={promo.offerId}>
                                                <td className="fw-bold">{promo.code}</td>
                                                <td className="text-truncate" style={{ maxWidth: 180 }} title={promo.name}>{promo.name}</td>
                                                <td>
                                                    {promo.discountType === 'percentage'
                                                        ? `${promo.discountValue}%`
                                                        : formatCurrency(promo.discountValue)}
                                                </td>
                                                <td>{formatCurrency(promo.minimumCart)}</td>
                                                <td>{promo.usageCount}</td>
                                                <td>{promo.uniqueUsers}</td>
                                                <td>
                                                    <span className={`badge ${promo.isActive && promo.approved ? 'bg-success' : 'bg-secondary'}`}>
                                                        {promo.isActive && promo.approved ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(promo.expiryDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default PromoAnalytics;
